import React, { useRef, useState, useEffect } from 'react';
import PianoKey, { KeyState } from './PianoKey';
import { Note } from '../../types/note';
import { isWhiteKey } from '../../utils/noteUtils';
import { getHint } from '../../utils/chordUtils';
import { useGameContext } from '../../contexts/GameContext';
import { useMidiContext } from '../../contexts/MidiContext';
import { useVirtualKeyboard } from '../../contexts/VirtualKeyboardContext';
import { TASK_TIMEOUT } from '../../constants';

let soundUtils: typeof import('../../utils/soundUtils') | null = null;

// Standard sizes and configurations
const BASE_WHITE_KEY_WIDTH = 25; // in pixels
const KEYBOARD_SIZES = [25, 37, 49, 61, 88];
const KEYBOARD_RANGES = {
  25: { start: 48, end: 72 },   // C3 to C5
  37: { start: 48, end: 84 },   // C3 to C6
  49: { start: 36, end: 84 },   // C2 to C6
  61: { start: 36, end: 96 },   // C2 to C7
  88: { start: 21, end: 108 },  // A0 to C8
};

const PianoKeyboard: React.FC = () => {
  const { tasks, taskIndex } = useGameContext();
  const { activeNotes: midiActiveNotes, isEnabled: isMidiEnabled } = useMidiContext();
  const { activeNotes: virtualActiveNotes, toggleClickNote, addTouchNote, removeTouchNote } = useVirtualKeyboard();
  const containerRef = useRef<HTMLDivElement>(null);
  const keyboardRef = useRef<HTMLDivElement>(null);
  const [keyWidths, setKeyWidths] = useState({
    white: BASE_WHITE_KEY_WIDTH,
    black: BASE_WHITE_KEY_WIDTH * 0.6 });
  const [keyboardSize, setKeyboardSize] = useState(49); // Default size
  const [notes, setNotes] = useState<Note[]>([]);
  const [hintedNotes, setHintedNotes] = useState<Note[]>([]);
  const [currentTaskTimedOut, setCurrentTaskTimedOut] = useState(false);
  const pointerTargets = useRef(new Map<number, Note>());

  const activeNotes = [...midiActiveNotes, ...virtualActiveNotes];

  // Get current task
  const currentTask = tasks.get(taskIndex);
  const currentChord = currentTask?.chord;
  const isClosed = currentTask?.isClosed || false;

  useEffect(() => {
    // Check initial timeout status
    const isTimedOut = currentTask ? (Date.now() - currentTask.startTime > TASK_TIMEOUT * 1000) : false;
    setCurrentTaskTimedOut(isTimedOut);

    // Setup interval to check timeout
    const timeoutCheckInterval = setInterval(() => {
      const isTimedOut = currentTask ? (Date.now() - currentTask.startTime > TASK_TIMEOUT * 1000) : false;
      setCurrentTaskTimedOut(isTimedOut);
    }, 100);

    return () => clearInterval(timeoutCheckInterval);
  }, [currentTask, setCurrentTaskTimedOut]);

  // Calculate appropriate keyboard size and adjust notes
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const keyboardSize = containerWidth / BASE_WHITE_KEY_WIDTH / 0.8;
        const bestKeyboardSize = !isMidiEnabled ? 25 : KEYBOARD_SIZES.reduce((prev, curr) =>
          Math.abs(curr - keyboardSize) < Math.abs(prev - keyboardSize) ? curr : prev
        );

        const { start, end } = KEYBOARD_RANGES[bestKeyboardSize as keyof typeof KEYBOARD_RANGES];
        setNotes(Array.from({ length: end - start + 1 }, (_, i) => Note.fromNumber(i + start)));

        const whiteKeyCount = Array.from({ length: end - start + 1 }, (_, i) => Note.fromNumber(i + start))
          .filter(note => isWhiteKey(note)).length;
        const adjustedWhiteKeyWidth = containerWidth / whiteKeyCount;

        setKeyboardSize(bestKeyboardSize);
        setKeyWidths({
          white: adjustedWhiteKeyWidth,
          black: adjustedWhiteKeyWidth * 0.6
        });
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMidiEnabled]);

  // Update hinted notes when target chord changes
  useEffect(() => {
    if (isClosed || !currentChord || !currentTaskTimedOut) {
      setHintedNotes([]);
      return;
    }

    const keyboardRange = KEYBOARD_RANGES[keyboardSize as keyof typeof KEYBOARD_RANGES];
    const hintedNotes: Note[] = getHint(currentChord, keyboardRange);
    setHintedNotes(hintedNotes);
  }, [currentChord, keyboardSize, isClosed, currentTaskTimedOut]);

  const isNoteActive = (note: Note) => {
    return activeNotes.some(activeNote => activeNote.number === note.value);
  };

  const keyState = (note: Note) => {
    if (isNoteActive(note)) {
      if (currentChord === undefined) {
        return KeyState.Active;
      }
      // Call notes() to get the NoteSet, then check with has()
      else if (currentChord.notes.has(note.norm())) {
        return KeyState.Correct;
      }
      else {
        return KeyState.Wrong;
      }
    }
    if (hintedNotes.includes(note)) {
      return KeyState.Hinted;
    }
    return KeyState.Default;
  }

  const findNoteFromElement = (element: HTMLElement): Note | null => {
    const noteClass = element.className.split(' ').find(cls => cls.startsWith('note-'));
    if (!noteClass) {
      return null;
    }
    const noteNumber = parseInt(noteClass.split('-')[1], 10);
    return Note.fromNumber(noteNumber);
  }

  const handlePointerDown = async (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) {
      return; // Only process left mouse button
    }

    e.preventDefault();

    const element = e.target as HTMLElement;
    const note = findNoteFromElement(element);

    if (!note) {
      return;
    }

    if (!soundUtils) {
      soundUtils = await import('../../utils/soundUtils');
    }

    // Track this pointer
    pointerTargets.current.set(e.pointerId, note);

    // Update state based on input type
    if (e.pointerType === 'mouse') {
      const state = keyState(note);
      if (state === KeyState.Default || state === KeyState.Hinted) {
        soundUtils.playNote(note, 0.5);
      }
      toggleClickNote(note);
    } else {
      soundUtils.playNote(note, 100);
      addTouchNote(note);
    }

    // Capture pointer to get events outside the element
    if (keyboardRef.current) {
      keyboardRef.current.setPointerCapture(e.pointerId);
    }
  };

  const handlePointerUp = async (e: React.PointerEvent) => {
    e.preventDefault();

    // For touch/pen inputs, we need to handle release
    if (e.pointerType !== 'mouse') {
      const note = pointerTargets.current.get(e.pointerId);

      if (note) {
        if (!soundUtils) {
          soundUtils = await import('../../utils/soundUtils');
        }

        soundUtils.stopNote(note.value);
        removeTouchNote(note);
        pointerTargets.current.delete(e.pointerId);
      }
    }

    // Release pointer capture
    if (keyboardRef.current) {
      keyboardRef.current.releasePointerCapture(e.pointerId);
    }
  };

  const handlePointerCancel = (e: React.PointerEvent) => {
    // Handle same as pointer up
    handlePointerUp(e);
  };

  const handlePointerMove = async (e: React.PointerEvent) => {
    // Only process for touch/pen, not mouse
    if (e.pointerType === 'mouse') {
      return;
    }

    e.preventDefault();

    if (!soundUtils) {
      soundUtils = await import('../../utils/soundUtils');
    }

    const oldNote = pointerTargets.current.get(e.pointerId);

    // Find the element at the current pointer position
    const element = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement;
    const newNote = findNoteFromElement(element);

    // If moved from a note to something else (or to no note)
    if (oldNote && (!newNote || oldNote.value !== newNote.value)) {
      soundUtils.stopNote(oldNote.value);
      removeTouchNote(oldNote);
      pointerTargets.current.delete(e.pointerId);
    }

    // If moved to a new note
    if (newNote && (!oldNote || oldNote.value !== newNote.value)) {
      pointerTargets.current.set(e.pointerId, newNote);
      soundUtils.playNote(newNote, 100);
      addTouchNote(newNote);
    }
  };

  return (
    <div className="w-full overflow-x-auto prevent-select" ref={containerRef}>
      <div className="flex justify-center my-8">
        <div className="flex relative"
          ref={keyboardRef}
          role="button"
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onPointerMove={handlePointerMove}
          style={{ touchAction: 'none' }}
        >
          {notes.map((note) => (
            <PianoKey
              key={note.value}
              note={note}
              state={keyState(note)}
              whiteKeyWidth={keyWidths.white}
              blackKeyWidth={keyWidths.black}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PianoKeyboard;
